import React from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import Root from '@src/routes/root';
import ErrorPage from '@src/routes/error-page';
import PointOnline from '@src/pages/point/online/online';
import PointOffline from '@src/pages/point/offline/offline';
import PointList from '@src/pages/point/list/list';
import { ModeType } from '@type/pointType';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AxiosError } from 'axios';
import { queryErrorHandler } from '@library/queryAPI';
import { CategoryAdd, MenuDetail, MenuAdd, ConnectOption, MenuExpose, MenuSort } from '@src/pages/product/menu';
import MenuProduct from '@src/pages/product/list';
import { OptionGroupAdd, OptionName, OptionSort, OptionStatus, OptionUnconnect } from '@src/pages/product/option';
import Recommend from './pages/product/recommend';
import PointOnlineDetail from './pages/point/online/detail';
import PointOnlineAdd from './pages/point/online/add';
import PointAmountSetting from './pages/point/amount/amount';
import PointOfflineEdit from './pages/point/offline/edit';
import PointAmountEdit from './pages/point/amount/edit';
import PointListDetail from './pages/point/list/detail';
import DownloadPomi from './pages/download/pomi';
import Logout from './pages/login/out';
import LoginCheck from './pages/login/check';
import ServiceTerms from './pages/terms/service';
import PrivacyTerms from './pages/terms/privacy';
import { TableOrder } from './pages/table/setting/setting';
import { TableOrderEdit } from './pages/table/setting/edit';
import { TableMenuPreview } from './pages/table/setting/preview';
import { TableList } from './pages/table/list/list';
import { TableListAdd } from './pages/table/list/add';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      cacheTime: 1000 * 60 * 5,
      retry: 0,
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error: AxiosError) => queryErrorHandler(error),
  }),
  mutationCache: new MutationCache({
    onError: (error: AxiosError) => queryErrorHandler(error),
  }),
});

const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: 'point',
        children: [
          {
            path: 'online',
            loader: () => {
              return { title: '자사앱 포인트 설정', isNeedLogin: true, useSiteID: true };
            },
            children: [
              {
                index: true,
                element: <PointOnline />,
              },
              {
                path: 'detail/:eventID',
                element: <PointOnlineDetail />,
                loader: () => {
                  return { title: '자사앱 포인트 상세', isNeedLogin: true, backUrl: true };
                },
              },
              {
                path: 'add/*',
                element: <PointOnlineAdd mode={ModeType.new} />,
                loader: () => {
                  return {
                    title: '자사앱 포인트 등록',
                    isNeedLogin: true,
                    backUrl: true,
                    message: '페이지를 벗어나시겠습니까?\n입력하신 내용은 저장되지 않습니다.',
                  };
                },
              },
              {
                path: 'edit/:eventID/*',
                element: <PointOnlineAdd mode={ModeType.edit} />,
                loader: () => {
                  return {
                    title: '자사앱 포인트 수정',
                    isNeedLogin: true,
                    backUrl: true,
                    message: '페이지를 벗어나시겠습니까?\n입력하신 내용은 저장되지 않습니다.',
                  };
                },
              },
            ],
          },
          {
            path: 'offline',
            loader: () => {
              return { title: '오프라인 포인트 설정', isNeedLogin: true, useSiteID: true };
            },
            children: [
              {
                index: true,
                element: <PointOffline />,
              },
              {
                path: ':storeID',
                element: <PointOfflineEdit />,
                loader: () => {
                  return { title: '오프라인 포인트 설정', isNeedLogin: true };
                },
              },
            ],
          },
          {
            path: 'list',
            loader: () => {
              return { title: '포인트 내역', isNeedLogin: true, useSiteID: true };
            },
            children: [
              {
                index: true,
                element: <PointList />,
              },
              {
                path: ':storeID',
                element: <PointListDetail />,
                loader: () => {
                  return { title: '포인트 내역 상세', isNeedLogin: true, backUrl: true };
                },
              },
            ],
          },
          {
            path: 'amount',
            loader: () => {
              return { title: '최소 사용 금액 설정', isNeedLogin: true, useSiteID: true };
            },
            children: [
              {
                index: true,
                element: <PointAmountSetting />,
              },
              {
                path: ':storeID',
                element: <PointAmountEdit />,
                loader: () => {
                  return { title: '최소 사용 금액 설정', isNeedLogin: true };
                },
              },
            ],
          },
        ],
      },
      {
        path: 'download',
        children: [
          {
            path: 'pomi',
            element: <DownloadPomi />,
            loader: () => {
              return { title: '포포 다운로드', isNeedLogin: false };
            },
          },
        ],
      },
      {
        path: 'table',
        children: [
          {
            path: 'setting',
            loader: () => {
              return { title: '테이블 주문 설정', isNeedLogin: true };
            },
            children: [
              {
                index: true,
                element: <TableOrder />,
              },
              {
                path: ':storeID',
                element: <TableOrderEdit />,
                loader: () => {
                  return { title: '테이블 주문 설정', isNeedLogin: true };
                },
              },
              {
                path: 'preview',
                element: <TableMenuPreview />,
                loader: () => {
                  return { title: '메뉴판 미리보기', backUrl: true, isNeedLogin: false };
                },
              },
            ],
          },
          {
            path: 'list',
            loader: () => {
              return { title: '테이블 관리', isNeedLogin: true, wrapClassName: 'table' };
            },
            children: [
              {
                index: true,
                element: <TableList />,
              },
              {
                path: 'add/:storeID/*',
                element: <TableListAdd />,
                loader: () => {
                  return { title: '테이블 일괄 추가', backUrl: true, isNeedLogin: true };
                },
              },
            ],
          },
        ],
      },
      {
        path: 'product',
        children: [
          {
            path: 'list',
            element: <MenuProduct />,
            loader: () => {
              return { title: '메뉴 관리', isNeedLogin: true };
            },
          },
          {
            path: 'category',
            element: <CategoryAdd />,
            loader: ({ request }) => {
              const url = new URL(request.url);
              const categoryID = url.searchParams.get('categoryID');
              const title = categoryID ? '카테고리 정보 수정' : '카테고리 추가';
              return {
                title,
                isNeedLogin: true,
                backUrl: true,
                message: '페이지를 벗어나시겠습니까?\n입력하신 내용은 저장되지 않습니다.',
              };
            },
          },
          {
            path: 'info',
            element: <MenuDetail />,
            loader: () => {
              return { title: '메뉴 상세 정보', isNeedLogin: true, backUrl: true, titleCenter: true };
            },
          },
          {
            path: 'form',
            element: <MenuAdd />,
            loader: ({ request }) => {
              const url = new URL(request.url);
              const categoryID = url.searchParams.get('categoryID');
              const title = categoryID ? '메뉴 추가' : '메뉴 정보 수정';
              return {
                title,
                isNeedLogin: true,
                backUrl: true,
                titleCenter: true,
                message: '페이지를 벗어나시겠습니까?\n입력하신 내용은 저장되지 않습니다.',
              };
            },
          },
          {
            path: 'connect',
            element: <ConnectOption />,
            loader: () => {
              return { title: '옵션 연결', isNeedLogin: true, backUrl: true, titleCenter: true };
            },
          },
          {
            path: 'status',
            element: <MenuExpose />,
            loader: () => {
              return { title: '메뉴 노출 · 품절 설정', isNeedLogin: true, backUrl: true, titleCenter: true };
            },
          },
          {
            path: 'sort',
            element: <MenuSort />,
            loader: ({ request }) => {
              const url = new URL(request.url);
              const categoryID = url.searchParams.get('categoryID');
              const productID = url.searchParams.get('productID');
              const title = !categoryID && !productID ? '카테고리' : categoryID && !productID ? '메뉴' : '옵션 그룹';
              return {
                title: `${title} 순서 수정`,
                isNeedLogin: true,
                backUrl: true,
                titleCenter: true,
                message: '페이지를 벗어나시겠습니까?\n입력하신 내용은 저장되지 않습니다.',
              };
            },
          },
          {
            path: 'option',
            element: <OptionGroupAdd />,
            loader: ({ request }) => {
              const url = new URL(request.url);
              const title = url.searchParams.has('options')
                ? '옵션추가'
                : url.searchParams.has('selectType')
                ? '선택 가능 옵션 수량 설정'
                : url.searchParams.has('title')
                ? '옵션그룹명 추가'
                : '옵션그룹 추가';
              return {
                title,
                isNeedLogin: true,
                backUrl: true,
                titleCenter: true,
                message: '페이지를 벗어나시겠습니까?\n입력하신 내용은 저장되지 않습니다.',
              };
            },
          },
          {
            path: 'option-status',
            element: <OptionStatus />,
            loader: ({ request }) => {
              const url = new URL(request.url);
              const title = url.searchParams.has('optionID') ? '옵션 노출 · 품절 설정' : '옵션그룹 노출 설정';
              return { title, isNeedLogin: true, backUrl: true, titleCenter: true };
            },
          },
          {
            path: 'option-sort',
            element: <OptionSort />,
            loader: () => {
              return {
                title: '옵션 순서 설정',
                isNeedLogin: true,
                backUrl: true,
                titleCenter: true,
                message: '페이지를 벗어나시겠습니까?\n입력하신 내용은 저장되지 않습니다.',
              };
            },
          },
          {
            path: 'option-unconnect',
            element: <OptionUnconnect />,
            loader: () => {
              return { title: '연결 메뉴 모두 해제', isNeedLogin: true, backUrl: true, titleCenter: true };
            },
          },
          {
            path: 'option-name',
            element: <OptionName />,
            loader: ({ request }) => {
              const url = new URL(request.url);
              const title = url.searchParams.has('optionID') ? '옵션명 및 가격 수정' : '옵션 그룹명 수정';
              return {
                title,
                isNeedLogin: true,
                backUrl: true,
                titleCenter: true,
                message: '페이지를 벗어나시겠습니까?\n입력하신 내용은 저장되지 않습니다.',
              };
            },
          },
          {
            path: 'recommend',
            element: <Recommend />,
            loader: () => {
              return { title: '추천 메뉴 선택', isNeedLogin: true, backUrl: true, titleCenter: true };
            },
          },
        ],
      },
    ],
  },
  {
    path: 'login',
    children: [
      { path: 'check', element: <LoginCheck /> },
      { path: 'out', element: <Logout /> },
    ],
  },
  {
    path: 'terms',
    children: [
      { path: 'service', element: <ServiceTerms /> },
      { path: 'privacy', element: <PrivacyTerms /> },
    ],
  },
]);

const App = (): React.ReactElement => {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default App;
